// Files — uploads into object storage (spec/08 §4.19, spec/03 §7.4, spec/04 §11, F-ORG-01 rule 4).
//
//   POST /v1/organizations/{o}/files                    reserve a file and get a signed URL to PUT it to
//   POST /v1/organizations/{o}/files/{fileId}/complete  "I have uploaded it": the server checks the
//                                                       object is there, the declared size and an
//                                                       image, and the worker makes variants
//   GET  /v1/organizations/{o}/files/{fileId}           the file, its status and its variants
//
// The bytes never pass through the api. The client PUTs straight to object storage — Cloudflare R2
// in production, MinIO in `local` — with a short-lived signed URL whose signature covers the
// declared `Content-Type` and `Content-Length`, so what arrives is what was asked for. Public media
// is then served from the CDN, not from this endpoint.
//
// **Two purposes**, both into the `public-media` bucket:
//
//   venue_photo        a venue's photo. Names the venue (`venueId`), needs the routes' own
//                      `venues:update` (to upload and complete) and `venues:read` (to read back), and
//                      is attached by `PATCH /v1/organizations/{o}/venues/{id}` with the file's id.
//   organization_logo  an organisation's logo for its venues' lock screens and receipts (spec/03
//                      §12.3, F-ORG-04). Belongs to no venue — `venueId` and `X-Clavian-Venue` are
//                      refused — at most 2 MB, and needs, **as well as** the route's permission, the
//                      purpose's own: `settings:kiosk`, the permission that sets `org.branding`, from a
//                      member whose staff access covers every venue. Attached through `org.branding`.
//
//   venue_logo         one venue's own logo, for its stations' lock screen and the kiosk's session rail
//                      (F-ORG-01, spec/13 §13). Names the venue, at most 2 MB, needs the routes' own
//                      `venues:update`; attached by `PATCH …/venues/{id}` with `logoFileId`.
//   product_image      a menu product's picture for Sell and the kiosk's Food screen (F-FNB-01). The
//                      organisation's (the menu is), at most 5 MB, and needs `products:manage` on top of the
//                      route's permission; attached by `POST`/`PATCH …/products` with `imageFileId`.
//
// The route's `x-clavian-permission` is the floor every upload needs; `x-clavian-purpose-permission`
// names what a purpose needs on top of it (`FILE_PURPOSE_PERMISSIONS`). Avatars and title art are
// purposes of their own when their features land.
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { FileIdSchema, OrganizationIdSchema, VenueIdSchema } from "../ids/schema.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { commonProblemResponses, JSON_CONTENT_TYPE, problemResponse } from "../openapi/responses.js";
import { PRODUCTS_MANAGE, SETTINGS_KIOSK, VENUES_READ, VENUES_UPDATE } from "../permissions/index.js";
import { MAX_IMAGE_UPLOAD_BYTES, MAX_LOGO_UPLOAD_BYTES, MAX_PRODUCT_IMAGE_UPLOAD_BYTES } from "../primitives/uploads.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { ImageVariantSchema } from "./venues.js";
export const FILE_PURPOSES = ['venue_photo', 'organization_logo', 'venue_logo', 'product_image'];
export const FilePurposeSchema = z.enum(FILE_PURPOSES).openapi('FilePurpose', {
    description: '`venue_photo`: a venue’s photo. `organization_logo`: an organisation’s logo for its venues’ lock screens and receipts. ' +
        '`venue_logo`: one venue’s own logo, for its stations’ lock screen and the kiosk. `product_image`: a menu product’s picture, ' +
        'for Sell and the kiosk’s Food screen.',
});
/**
 * The OpenAPI extension an upload route declares beside its `x-clavian-permission`: what each purpose
 * needs **as well as** the route's own permission. A purpose absent from the map needs nothing more.
 */
export const FILE_PURPOSE_PERMISSION_EXTENSION = 'x-clavian-purpose-permission';
/** What each purpose needs on top of the route's permission, to upload and to complete. */
export const FILE_PURPOSE_PERMISSIONS = {
    // The permission that sets `org.branding`: uploading a logo is the first half of setting one.
    organization_logo: SETTINGS_KIOSK,
    // The permission that edits the menu: a product's picture is the first half of setting it (F-FNB-01).
    product_image: PRODUCTS_MANAGE,
};
/** Purposes that belong to a venue, and must name one. */
export const VENUE_FILE_PURPOSES = ['venue_photo', 'venue_logo'];
/** The image types a venue photo may be. SVG is not one: it is a document that can carry script. */
export const IMAGE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ImageContentTypeSchema = z.enum(IMAGE_CONTENT_TYPES).openapi('ImageContentType');
export { MAX_IMAGE_UPLOAD_BYTES, MAX_LOGO_UPLOAD_BYTES, MAX_PRODUCT_IMAGE_UPLOAD_BYTES };
/** The most one upload of each purpose may be. */
export const MAX_UPLOAD_BYTES = {
    venue_photo: MAX_IMAGE_UPLOAD_BYTES,
    organization_logo: MAX_LOGO_UPLOAD_BYTES,
    venue_logo: MAX_LOGO_UPLOAD_BYTES,
    product_image: MAX_PRODUCT_IMAGE_UPLOAD_BYTES,
};
/** The widths the worker makes, in pixels, smallest first (spec/03 §7.4 "image variants made by the worker"). */
export const IMAGE_VARIANT_WIDTHS = [320, 640, 1280, 1920];
export const FILE_STATUSES = ['pending', 'uploaded', 'ready', 'failed'];
export const FileStatusSchema = z.enum(FILE_STATUSES).openapi('FileStatus', {
    description: '`pending` until the upload is completed, `uploaded` once the server has checked it, `ready` once the ' +
        'worker has made its variants, `failed` if the object was missing or not what was declared — or if the ' +
        'upload was still not completed an hour after it started, when it is removed unchecked.',
});
export const FileSchema = z
    .object({
    id: FileIdSchema,
    purpose: FilePurposeSchema,
    venueId: VenueIdSchema.nullable(),
    contentType: ImageContentTypeSchema,
    bytes: z.int().positive(),
    status: FileStatusSchema,
    /** Where the original is served from. Public media only; a private file never has one. */
    url: z.url(),
    variants: z.array(ImageVariantSchema),
    createdAt: IsoDateTimeSchema,
})
    .openapi('File');
export const CreateFileSchema = z
    .object({
    purpose: FilePurposeSchema,
    /** The venue the image is for: required for `venue_photo` and `venue_logo`, refused for the organisation's. */
    venueId: VenueIdSchema.optional(),
    contentType: ImageContentTypeSchema,
    bytes: z.int().positive().max(MAX_IMAGE_UPLOAD_BYTES),
})
    .strict()
    .superRefine((body, context) => {
    const ofVenue = VENUE_FILE_PURPOSES.includes(body.purpose);
    if (ofVenue && body.venueId === undefined) {
        context.addIssue({ code: 'custom', path: ['venueId'], message: `A ${body.purpose} names the venue it is for.` });
    }
    if (!ofVenue && body.venueId !== undefined) {
        context.addIssue({ code: 'custom', path: ['venueId'], message: `A ${body.purpose} belongs to the organisation, not to a venue.` });
    }
    const max = MAX_UPLOAD_BYTES[body.purpose];
    if (max !== undefined && body.bytes > max) {
        context.addIssue({ code: 'custom', path: ['bytes'], message: `A ${body.purpose} is at most ${max} bytes.` });
    }
})
    .openapi('CreateFile');
/** Where and how to PUT the bytes: a signed URL, and the headers its signature covers. */
export const SignedUploadSchema = z
    .object({
    method: z.enum(['PUT']),
    url: z.url(),
    /** Send exactly this with the PUT; the signature covers it, and the body's length. */
    contentType: ImageContentTypeSchema,
    expiresAt: IsoDateTimeSchema,
})
    .openapi('SignedUpload');
export const FileUploadSchema = z
    .object({
    file: FileSchema,
    upload: SignedUploadSchema,
})
    .openapi('FileUpload');
const FileParamsSchema = z
    .object({ organizationId: OrganizationIdSchema, fileId: FileIdSchema })
    .openapi('FileParams');
const forbidden = problemResponse('Your role does not allow this.', 'forbidden', 'second-factor-required');
export const createFileRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/files',
    operationId: 'createFile',
    tags: ['files'],
    summary: 'Start an upload',
    description: 'Reserves the file and returns a signed URL, valid for 15 minutes, to PUT the bytes to. Complete it ' +
        'within the hour: a file still `pending` an hour after it started is marked `failed` and whatever was ' +
        'uploaded to it is removed. Requires `venues:update`. A `venue_photo` names a venue of this organisation. An ' +
        '`organization_logo` names none, is at most 2 MB, and also requires `settings:kiosk` from a member whose staff ' +
        'access covers every venue. A `venue_logo` names a venue and is at most 2 MB. A `product_image` names none, is at ' +
        'most 5 MB, and also requires `products:manage` from a member whose staff access covers every venue.',
    [PERMISSION_EXTENSION]: VENUES_UPDATE,
    [FILE_PURPOSE_PERMISSION_EXTENSION]: FILE_PURPOSE_PERMISSIONS,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { [JSON_CONTENT_TYPE]: { schema: CreateFileSchema } }, required: true },
    },
    responses: {
        201: { description: 'The file, pending, and where to upload it.', content: { [JSON_CONTENT_TYPE]: { schema: FileUploadSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such venue in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeFileRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/files/{fileId}/complete',
    operationId: 'completeFile',
    tags: ['files'],
    summary: 'Finish an upload',
    description: 'Checks the object is in storage with the declared size and type and is really an image, then hands ' +
        'it to the worker to make its variants. Completing twice is harmless. Requires what starting the ' +
        'upload did: `venues:update`, `settings:kiosk` for an `organization_logo` and `products:manage` for a `product_image`.',
    [PERMISSION_EXTENSION]: VENUES_UPDATE,
    [FILE_PURPOSE_PERMISSION_EXTENSION]: FILE_PURPOSE_PERMISSIONS,
    request: { params: FileParamsSchema },
    responses: {
        200: { description: 'The file, uploaded.', content: { [JSON_CONTENT_TYPE]: { schema: FileSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such file in this organisation.', 'not-found'),
        422: problemResponse('Nothing was uploaded, or it is not what was declared.', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getFileRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/files/{fileId}',
    operationId: 'getFile',
    tags: ['files'],
    summary: 'Read a file',
    description: 'Its status and, once the worker has run, its variants. Requires `venues:read`.',
    [PERMISSION_EXTENSION]: VENUES_READ,
    request: { params: FileParamsSchema },
    responses: {
        200: { description: 'The file.', content: { [JSON_CONTENT_TYPE]: { schema: FileSchema } } },
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: forbidden,
        404: problemResponse('No such file in this organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=files.js.map